import { useEffect } from 'react'

import { pingApiCall } from '@/entities/Ping/PingService'
import { formatDate } from '@/helpers/date'
import { delay } from '@/helpers/delay'
import { setIsOnline, setServerTime } from '@/redux/app/appReducer'
import { useTypedDispatch } from '@/store'
import { DateFormat } from '@/types'

// The delay after a poll before calling the next poll
export const pollingDelay = 3000

// Threshold of a poll call to be considered a timeout
export const pollingTimeoutThreshold = 5000

// The amount of successive success / fail calls it takes to switch between offline / online
export const pollingSwitchThreshold = 2

const getLocalApiDateTime = (): string => formatDate(new Date(), DateFormat.apiDateTime)

// Pings the API to keep the online status and server time in the store up to date. The bookkeeping lives in local
// variables instead of React state, so a poll only re-renders the components that read the server time.
export const useOnlinePolling = (): void => {
    const dispatch = useTypedDispatch()

    useEffect((): () => void => {
        let isSubscribed = true
        let isOnline = navigator.onLine
        let successes = 0
        let fails = 0

        dispatch(setIsOnline(isOnline))
        dispatch(setServerTime(getLocalApiDateTime()))

        const poll = async (): Promise<void> => {
            const ping = await pingApiCall(pollingTimeoutThreshold)

            if (! isSubscribed) {
                return
            }

            successes = ping ? successes + 1 : 0
            fails = ping ? 0 : fails + 1

            dispatch(setServerTime(ping ? ping.atom : getLocalApiDateTime()))

            const nextIsOnline = (successes >= pollingSwitchThreshold) || (isOnline && fails < pollingSwitchThreshold)

            if (nextIsOnline !== isOnline) {
                isOnline = nextIsOnline
                dispatch(setIsOnline(isOnline))
            }

            await delay(pollingDelay)

            if (isSubscribed) {
                poll()
            }
        }

        poll()

        return (): void => {
            isSubscribed = false
        }
    }, [dispatch])
}
